import React, { useState, useEffect } from 'react';
import { User, CreateUserData, UserRole } from '../../types';
import { ROLE_OPTIONS } from '../../utils/roles';

interface UserFormProps {
  user?: User | null;
  onSubmit: (data: CreateUserData) => void;
  onCancel: () => void;
  loading: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'secretary',
    roles: [],
    phone: '',
    password: '',
    specialite: '',
    numero_ordre: '',
  });

  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        roles: (user.roles || []).filter((r) => r !== user.role),
        phone: user.phone || '',
        password: '', // Ne pas pré-remplir le mot de passe
        specialite: user.specialite || '',
        numero_ordre: user.numero_ordre || '',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est requis';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Le nom est requis';
    }

    if (!user && !formData.password.trim()) {
      newErrors.password = 'Le mot de passe est requis pour un nouvel utilisateur';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!user && !passwordConfirm.trim()) {
      newErrors.password_confirm = 'La confirmation du mot de passe est requise';
    }

    if (formData.password && passwordConfirm && formData.password !== passwordConfirm) {
      newErrors.password_confirm = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Ajouter password_confirm aux données envoyées
      const submitData = { ...formData, password_confirm: passwordConfirm };
      onSubmit(submitData);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Nom d'utilisateur *
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.username ? 'border-red-300' : ''
                }`}
                disabled={loading}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.email ? 'border-red-300' : ''
                }`}
                disabled={loading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  Prénom *
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                    errors.first_name ? 'border-red-300' : ''
                  }`}
                  disabled={loading}
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Nom *
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                    errors.last_name ? 'border-red-300' : ''
                  }`}
                  disabled={loading}
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Rôle *
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-gray-900 sm:text-sm"
                disabled={loading}
              >
                <option value="secretary">Secrétaire</option>
                <option value="dentist">Dentiste</option>
                <option value="comptable">Comptable</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rôles additionnels
              </label>
              <p className="text-xs text-gray-500 mb-2">Un utilisateur peut cumuler plusieurs rôles.</p>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.filter((o) => o.value !== formData.role).map((opt) => {
                  const checked = (formData.roles || []).includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={loading}
                        onChange={(e) => {
                          const current = formData.roles || [];
                          const next = e.target.checked
                            ? [...current, opt.value]
                            : current.filter((r) => r !== opt.value);
                          setFormData((prev) => ({ ...prev, roles: next as UserRole[] }));
                        }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Téléphone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
                disabled={loading}
              />
            </div>

            {/* Champs spécialisés pour les dentistes */}
            {formData.role === 'dentist' && (
              <>
                <div>
                  <label htmlFor="specialite" className="block text-sm font-medium text-gray-700">
                    Spécialité
                  </label>
                  <select
                    id="specialite"
                    name="specialite"
                    value={formData.specialite}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-gray-900 sm:text-sm"
                    disabled={loading}
                  >
                    <option value="">Sélectionnez une spécialité</option>
                    <option value="Dentiste généraliste">Dentiste généraliste</option>
                    <option value="Orthodontiste">Orthodontiste</option>
                    <option value="Chirurgien-dentiste">Chirurgien-dentiste</option>
                    <option value="Parodontiste">Parodontiste</option>
                    <option value="Endodontiste">Endodontiste</option>
                    <option value="Prosthodontiste">Prosthodontiste</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="numero_ordre" className="block text-sm font-medium text-gray-700">
                    Numéro d'ordre professionnel
                  </label>
                  <input
                    type="text"
                    id="numero_ordre"
                    name="numero_ordre"
                    value={formData.numero_ordre}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
                    disabled={loading}
                    placeholder="Ex: 12345"
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {user ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-gray-900 sm:text-sm ${
                  errors.password ? 'border-red-300' : ''
                }`}
                disabled={loading}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {!user && (
              <div>
                <label htmlFor="password_confirm" className="block text-sm font-medium text-gray-700">
                  Confirmer le mot de passe *
                </label>
                <input
                  type="password"
                  id="password_confirm"
                  name="password_confirm"
                  value={passwordConfirm}
                  onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    if (errors.password_confirm) {
                      setErrors(prev => ({ ...prev, password_confirm: '' }));
                    }
                  }}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-gray-900 sm:text-sm ${
                    errors.password_confirm ? 'border-red-300' : ''
                  }`}
                  disabled={loading}
                />
                {errors.password_confirm && (
                  <p className="mt-1 text-sm text-red-600">{errors.password_confirm}</p>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : user ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;